/**
 * Utilidad para obtener los datos del agente del widget de forma consistente.
 * Prioriza widget_agent_data (resultado de Usuarios_Get ByGuid con el GUID del widget)
 * sobre 'user' (que puede venir del portal/SSO y no corresponder al agente del widget).
 */

const WIDGET_AGENT_DATA_KEY = 'widget_agent_data';
const USER_KEY = 'user';

export interface WidgetAgentDataForRequest {
  Id_Usuario: number;
  Id: number;
  Id_Persona: number | null;
  Id_Agente: number | null;
  Id_Oficina: number;
  UsuarioAdd: number;
  UsuarioUmod: number;
  UsuarioDel: number;
  Id_Usuario_Modificacion: number;
  user: number;
  UsuarioUMod: number;
}

const defaultAgentData = (): WidgetAgentDataForRequest => ({
  Id_Usuario: 1,
  Id: 1,
  Id_Persona: null,
  Id_Agente: null,
  Id_Oficina: 2,
  UsuarioAdd: 1,
  UsuarioUmod: 1,
  UsuarioDel: 1,
  Id_Usuario_Modificacion: 1,
  user: 1,
  UsuarioUMod: 1,
});

const parseId = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return null;
};

/**
 * Obtiene los datos del agente para usar en peticiones de cotización y emisión.
 * Prioriza widget_agent_data (agente autenticado por GUID) sobre 'user' (puede ser sesión del portal).
 */
export function getWidgetAgentData(): WidgetAgentDataForRequest {
  if (typeof window === 'undefined') return defaultAgentData();

  let idUsuario: number | null = null;
  let idPersona: number | null = null;
  let idAgente: number | null = null;
  let idOficina: number | null = null;

  try {
    const widgetStr = window.localStorage.getItem(WIDGET_AGENT_DATA_KEY);
    if (widgetStr) {
      const widget = JSON.parse(widgetStr) as Record<string, unknown>;
      idUsuario = parseId(widget.Id_Usuario);
      idPersona = parseId(widget.Id_Persona);
      idAgente = parseId(widget.Id_Agente);
    }
  } catch {
    // ignorar error de parseo
  }

  try {
    const userStr = window.localStorage.getItem(USER_KEY) || '{}';
    const user = JSON.parse(userStr) as Record<string, unknown>;
    if (idUsuario === null) idUsuario = parseId(user.Id_Usuario ?? user.Id);
    if (idPersona === null) idPersona = parseId(user.Id_Persona);
    if (idAgente === null) idAgente = parseId(user.Id_Agente);
    if (idOficina === null) idOficina = parseId(user.Id_Oficina);
  } catch {
    // ignorar error de parseo
  }

  const uid = idUsuario ?? 1;
  return {
    Id_Usuario: uid,
    Id: uid,
    Id_Persona: idPersona,
    Id_Agente: idAgente,
    Id_Oficina: idOficina ?? 2,
    UsuarioAdd: uid,
    UsuarioUmod: uid,
    UsuarioDel: uid,
    Id_Usuario_Modificacion: uid,
    user: uid,
    UsuarioUMod: uid,
  };
}

export interface WidgetSellerIdentity {
  Guid_Vendedor: string | null;
  Clave_Agente: string | null;
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const parseGuid = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return GUID_PATTERN.test(trimmed) ? trimmed : null;
};

const parseText = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text !== '' ? text : null;
};

/**
 * Seller identity required by p_Cotizaciones_Autos_Detalle_Insert (Guid_Vendedor or
 * Clave_Agente). Kept separate from getWidgetAgentData so it is not spread into
 * unrelated request payloads.
 */
export function getWidgetSellerIdentity(): WidgetSellerIdentity {
  const identity: WidgetSellerIdentity = { Guid_Vendedor: null, Clave_Agente: null };
  if (typeof window === 'undefined') return identity;

  try {
    const widgetStr = window.localStorage.getItem(WIDGET_AGENT_DATA_KEY);
    if (widgetStr) {
      const widget = JSON.parse(widgetStr) as Record<string, unknown>;
      identity.Guid_Vendedor = parseGuid(widget.Guid_Usuario);
      identity.Clave_Agente = parseText(widget.Clave_Agente);
    }
  } catch {
    // ignorar error de parseo
  }

  try {
    const user = JSON.parse(window.localStorage.getItem(USER_KEY) || '{}') as Record<string, unknown>;
    if (identity.Guid_Vendedor === null) identity.Guid_Vendedor = parseGuid(user.Guid);
    if (identity.Clave_Agente === null) identity.Clave_Agente = parseText(user.Clave_Agente);
  } catch {
    // ignorar error de parseo
  }

  return identity;
}
